import React, { useState, useContext, useEffect } from 'react';
import { BottomNavigation, BottomNavigationAction, Paper, Box, Typography, Divider } from '@mui/material';
import { 
  Dashboard, 
  TrendingUp, 
  Assessment, 
  AccountBalance, 
  Settings,
  AccountBalanceWallet
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { WebSocketContext } from '../WebSocketContext';
import './MasterNavBar.css';

function MasterNavBar() {
  const { isDarkMode } = useTheme();
  const { userDetails } = useContext(WebSocketContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [value, setValue] = useState(0);

  // Master navigation items
  const masterNavItems = [
    { 
      key: 'dashboard', 
      title: 'Dashboard', 
      path: '/master/dashboard', 
      icon: <Dashboard />, 
      index: 0 
    },
    { 
      key: 'watchlist', 
      title: 'Watchlist', 
      path: '/master/watchlist', 
      icon: <TrendingUp />, 
      index: 1 
    },
    { 
      key: 'trades', 
      title: 'Trades', 
      path: '/master/trades', 
      icon: <Assessment />, 
      index: 2 
    },
    { 
      key: 'net-positions', 
      title: 'Net Positions', 
      path: '/master/net-positions', 
      icon: <AccountBalanceWallet />, 
      index: 3 
    },
    { 
      key: 'portfolio', 
      title: 'Portfolio', 
      path: '/master/portfolio', 
      icon: <AccountBalance />, 
      index: 4 
    },
    { 
      key: 'settings', 
      title: 'Settings', 
      path: '/master/settings', 
      icon: <Settings />, 
      index: 5 
    }
  ];

  // Get current selected index based on pathname
  const getCurrentIndex = () => {
    const currentItem = masterNavItems.find(item => item.path === location.pathname);
    return currentItem ? currentItem.index : 0;
  };

  // Update selected when location changes
  useEffect(() => {
    const currentIndex = getCurrentIndex();
    setValue(currentIndex);
  }, [location.pathname]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    const selectedItem = masterNavItems.find(item => item.index === newValue);
    if (selectedItem) {
      navigate(selectedItem.path);
    }
  };

  return (
    <div className={`master-bottom-navigation ${isDarkMode ? 'dark-theme' : 'light-theme'}`} data-theme={isDarkMode ? 'dark' : 'light'}>
      {/* Desktop Sidebar */}
      <div className="master-desktop-sidebar">
        <div className="master-sidebar-header">
          <Typography variant="h6" className="master-sidebar-title">
            Master Panel
          </Typography>
          <Divider className="master-sidebar-divider" />
        </div>
        
        <div className="master-sidebar-nav">
          {masterNavItems.map((item) => (
            <div
              key={item.key}
              className={`master-sidebar-item ${location.pathname === item.path ? 'active' : ''}`}
              onClick={() => navigate(item.path)}
            >
              <div className="master-sidebar-icon">
                {item.icon}
              </div>
              <span className="master-sidebar-label">{item.title}</span>
            </div>
          ))}
        </div>

        {/* User Info */}
        <div className="master-sidebar-footer">
          <Divider className="master-sidebar-divider" />
          <div className="master-user-info">
            <Typography variant="body2" className="master-user-name">
              {userDetails?.clientName || 'Master User'}
            </Typography>
            <Typography variant="caption" className="master-user-role">
              Master • ID: {userDetails?.clientId || 'N/A'}
            </Typography>
          </div>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <Paper 
        className="master-mobile-nav" 
        sx={{ 
          position: 'fixed', 
          bottom: 0, 
          left: 0, 
          right: 0,
          backgroundColor: isDarkMode ? 'var(--bg-primary)' : '#ffffff',
          borderTop: isDarkMode ? '1px solid var(--border-color)' : '1px solid #e0e0e0'
        }} 
        elevation={3}
      >
        <BottomNavigation 
          value={value} 
          onChange={handleChange}
          sx={{
            backgroundColor: 'transparent',
            '& .MuiBottomNavigationAction-root': {
              color: isDarkMode ? 'var(--text-secondary)' : '#666666',
              '&.Mui-selected': {
                color: 'var(--accent-color)'
              }
            }
          }}
        >
          {masterNavItems.map((item) => (
            <BottomNavigationAction
              key={item.key}
              label={item.title}
              value={item.index}
              icon={item.icon}
            />
          ))}
        </BottomNavigation>
      </Paper>
    </div>
  );
}

export default MasterNavBar;
